import { useState } from "react";
import { Button, Tag, Tooltip, Title, Icon, Stack } from "@introist/react-foundation/v2";
import styled from "styled-components";
import { WellbeingEmployeeRow, WellbeingScenarioType, sortSignalsByPriority, formatPrimaryRisk } from "../hooks/useWellbeingData";
import { EmployeeMetricCard } from "./EmployeeMetricCard";

const TableContainer = styled.div`
  border: 1px solid var(--palette-border-default);
  border-radius: 8px;
  overflow: hidden;
  background: white;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 2fr 3fr 120px 150px 180px;
  background: var(--palette-background-surface);
  border-bottom: 2px solid var(--palette-border-default);
  padding: var(--spacing-small) var(--spacing-medium);
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--palette-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableRow = styled.div<{ $severity?: "medium" | "high"; $expanded?: boolean }>`
  display: grid;
  grid-template-columns: 40px 2fr 3fr 120px 150px 180px;
  align-items: center;
  padding: var(--spacing-medium);
  border-bottom: 1px solid var(--palette-border-default);
  border-left: 4px solid ${({ $severity }) =>
    $severity === "high" ? "var(--palette-danger-default)" : "var(--palette-warning-default)"};
  cursor: pointer;
  transition: background 0.15s ease;
  background: ${({ $expanded }) => $expanded ? "var(--palette-background-surface)" : "white"};

  &:hover {
    background: var(--palette-background-surface);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ExpandedContent = styled.div`
  grid-column: 1 / -1;
  padding: var(--spacing-large);
  background: var(--palette-background-default);
  border-top: 1px solid var(--palette-border-default);
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--spacing-medium);
  margin-bottom: var(--spacing-large);
`;

const EmployeeName = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--palette-primary-light);
  color: var(--palette-primary-default);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
`;

const EllipsisText = styled(Title)`
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SignalChip = styled(Tag)`
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
`;

const SeverityTag = styled(Tag)<{ $severity: "medium" | "high" }>`
  min-width: 68px;
  justify-content: center;
  color: ${({ $severity }) =>
    $severity === "high" ? "var(--palette-danger-default)" : "var(--palette-warning-default)"};
`;

const ExpandIconWrapper = styled.div<{ $expanded: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${({ $expanded }) => $expanded ? "90deg" : "0deg"});
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SectionTitle = styled(Title)`
  margin-bottom: var(--spacing-small);
  color: var(--palette-text-secondary);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const scenarioTypeTitles: Record<WellbeingScenarioType, string> = {
  PLUS_HOURS_HIGH: "Excess Overtime",
  MINUS_HOURS_LOW: "Hour Deficit",
  LONG_SICK_LEAVE: "Extended Sick Leave",
  SICK_LEAVES_12M_HIGH: "Frequent Sick Leaves",
  SHORT_SICK_LEAVES_3M_HIGH: "Recent Sick Leaves",
  VACATION_NOT_TAKEN: "Unused Vacation",
  BURNOUT_RISK_HIGH: "Burnout Risk",
  LOW_ENGAGEMENT: "Low Engagement",
  CONSECUTIVE_DAYS_HIGH: "No Time Off"
};

type EnhancedEmployeeTableProps = {
  rows: WellbeingEmployeeRow[];
  selectedWorkflowId?: string;
  onLaunchWorkflow: (employeeId: string) => Promise<void>;
  launchingEmployeeId: string | null;
};

export const EnhancedEmployeeTable = ({
  rows,
  selectedWorkflowId,
  onLaunchWorkflow,
  launchingEmployeeId
}: EnhancedEmployeeTableProps) => {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <TableContainer>
      <TableHeader>
        <div></div>
        <div>Employee</div>
        <div>Primary Risk</div>
        <div>Severity</div>
        <div>Signals</div>
        <div>Actions</div>
      </TableHeader>

      {rows.map(row => {
        const orderedSignals = sortSignalsByPriority(row.scenarios);
        const signalCount = orderedSignals.length;
        const signalsTooltip = orderedSignals
          .map(signal => scenarioTypeTitles[signal.type])
          .join(", ");
        const signalSummary = signalCount === 1
          ? scenarioTypeTitles[orderedSignals[0].type]
          : `${signalCount} signals`;
        const isExpanded = expandedRowId === row.employee.id;

        return (
          <div key={row.employee.id}>
            <TableRow
              $severity={row.topSeverity}
              $expanded={isExpanded}
              onClick={() => setExpandedRowId(isExpanded ? null : row.employee.id)}
            >
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ExpandIconWrapper $expanded={isExpanded}>
                  <Icon name="chevronRight" style={{ fontSize: 16 }} />
                </ExpandIconWrapper>
              </div>

              <EmployeeName>
                <Avatar>{getInitials(row.employee.name ?? "Unknown")}</Avatar>
                <span>{row.employee.name ?? "Unknown"}</span>
              </EmployeeName>

              <Tooltip tooltip={formatPrimaryRisk(orderedSignals[0])}>
                <EllipsisText>{formatPrimaryRisk(orderedSignals[0])}</EllipsisText>
              </Tooltip>

              <SeverityTag
                $severity={row.topSeverity}
                colorVariant={row.topSeverity === "high" ? "danger" : "warning"}
              >
                {row.topSeverity === "high" ? "High" : "Medium"}
              </SeverityTag>

              <Tooltip tooltip={signalsTooltip}>
                <SignalChip colorVariant="surface">{signalSummary}</SignalChip>
              </Tooltip>

              <div onClick={(e) => e.stopPropagation()}>
                <Button
                  size="small"
                  onClickWithLoading={() => row.employee.id ? onLaunchWorkflow(row.employee.id) : Promise.resolve()}
                  disabled={!selectedWorkflowId || !row.employee.id || launchingEmployeeId === row.employee.id}
                >
                  Launch workflow
                </Button>
              </div>
            </TableRow>

            {isExpanded && (
              <ExpandedContent>
                <Stack vertical gap="large">
                  <div>
                    <SectionTitle>Wellbeing Metrics</SectionTitle>
                    <MetricsGrid>
                      <EmployeeMetricCard
                        label="Hour Balance"
                        value={row.metrics.hourBalance > 0 ? `+${row.metrics.hourBalance}h` : `${row.metrics.hourBalance}h`}
                        icon="clock"
                        status={Math.abs(row.metrics.hourBalance) > 40 ? "danger" : Math.abs(row.metrics.hourBalance) > 20 ? "warning" : "good"}
                      />
                      <EmployeeMetricCard
                        label="Vacation Remaining"
                        value={`${row.metrics.vacationDaysRemaining} days`}
                        icon="calendar"
                        status={row.metrics.vacationDaysRemaining >= 20 ? "danger" : row.metrics.vacationDaysRemaining >= 15 ? "warning" : "good"}
                        subtext={`${row.metrics.vacationDaysTaken} days taken`}
                      />
                      <Tooltip tooltip="Burnout risk is calculated based on multiple factors including overtime hours, consecutive work days, weekend work, and meeting load. Higher scores indicate greater risk.">
                        <div>
                          <EmployeeMetricCard
                            label="Burnout Risk"
                            value={`${row.metrics.burnoutRiskScore}/100`}
                            icon="zap"
                            status={row.metrics.burnoutRiskScore >= 80 ? "danger" : row.metrics.burnoutRiskScore >= 60 ? "warning" : "good"}
                            showProgress
                            progressPercentage={row.metrics.burnoutRiskScore}
                            subtext="Based on work patterns"
                          />
                        </div>
                      </Tooltip>
                      <EmployeeMetricCard
                        label="Overtime (4 weeks)"
                        value={`${row.metrics.overtimeHoursLast4Weeks}h`}
                        icon="trendingUp"
                        status={row.metrics.overtimeHoursLast4Weeks >= 30 ? "danger" : row.metrics.overtimeHoursLast4Weeks >= 20 ? "warning" : "good"}
                      />
                      <EmployeeMetricCard
                        label="Consecutive Work Days"
                        value={`${row.metrics.consecutiveDaysWorked} days`}
                        icon="calendar"
                        status={row.metrics.consecutiveDaysWorked >= 15 ? "danger" : row.metrics.consecutiveDaysWorked >= 12 ? "warning" : "good"}
                      />
                    </MetricsGrid>
                  </div>

                  <div>
                    <SectionTitle>Sick Leave History</SectionTitle>
                    <MetricsGrid>
                      <EmployeeMetricCard
                        label="Current Sick Streak"
                        value={`${row.metrics.currentSickStreakDays} days`}
                        icon="activity"
                        status={row.metrics.currentSickStreakDays >= 21 ? "danger" : row.metrics.currentSickStreakDays >= 14 ? "warning" : "good"}
                      />
                      <EmployeeMetricCard
                        label="Sick Leaves (12m)"
                        value={`${row.metrics.sickLeaves12m} times`}
                        icon="alertCircle"
                        status={row.metrics.sickLeaves12m >= 7 ? "danger" : row.metrics.sickLeaves12m >= 4 ? "warning" : "good"}
                      />
                      <EmployeeMetricCard
                        label="Short Sick Leaves (3m)"
                        value={`${row.metrics.shortSickLeaves3m} times`}
                        icon="alertTriangle"
                        status={row.metrics.shortSickLeaves3m >= 5 ? "danger" : row.metrics.shortSickLeaves3m >= 3 ? "warning" : "good"}
                      />
                    </MetricsGrid>
                  </div>

                  <div>
                    <SectionTitle>Work Pattern Indicators</SectionTitle>
                    <MetricsGrid>
                      <EmployeeMetricCard
                        label="Weekend Work"
                        value={`${row.metrics.weekendWorkInstances} times`}
                        icon="calendar"
                        status={row.metrics.weekendWorkInstances >= 6 ? "danger" : row.metrics.weekendWorkInstances >= 4 ? "warning" : "good"}
                        subtext="Last 4 weeks"
                      />
                      <EmployeeMetricCard
                        label="Meeting Load"
                        value={`${row.metrics.meetingLoadScore}/100`}
                        icon="users"
                        status={row.metrics.meetingLoadScore >= 80 ? "danger" : row.metrics.meetingLoadScore >= 70 ? "warning" : "good"}
                        showProgress
                        progressPercentage={row.metrics.meetingLoadScore}
                      />
                      <EmployeeMetricCard
                        label="Days Since Vacation"
                        value={`${row.metrics.daysSinceLastVacation} days`}
                        icon="sun"
                        status={row.metrics.daysSinceLastVacation >= 120 ? "danger" : row.metrics.daysSinceLastVacation >= 90 ? "warning" : "good"}
                      />
                    </MetricsGrid>
                  </div>
                </Stack>
              </ExpandedContent>
            )}
          </div>
        );
      })}
    </TableContainer>
  );
};
